import CssBaseline from '@mui/material/CssBaseline';
import {createTheme, StyledEngineProvider, ThemeProvider} from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {history} from 'common/history';
import LoaderContainer from "containers/LoaderContainer";
import React, { useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";


import {Provider} from "react-redux";
import {Router} from "react-router";

import App from "./App";
import Navbar from "./components/navbar/navbar";
import configureStore from "./stores/configureStore";
import ChatwootWidget from "components/ChatwootWidget";
import PushNotificationContainer from "containers/PushNotificationContainer";

const {store} = configureStore();

// v4's Backdrop sat behind the modal's children on its own; v5 leaves it in
// normal flow, where it paints over any child that is not itself positioned.
const theme = createTheme({
  components: {
    MuiBackdrop: {
      styleOverrides: {
        root: {zIndex: -1},
      },
    },
  },
});

// Routes whose pages carry their own skeletons — the global spinner would only
// sit on top of them.
const SKELETON_ROUTES = [
  '/mentorship', '/chat', '/learninghub', '/session', '/surveylisting', '/survey', '/stage', '/moreinfo',
  // The auth screens show progress on the button that started the work.
  '/signin', '/emailverify', '/resetpassword', '/signup',
];

// One scroll in the app, and it belongs to the content. The nav bar sits above
// it and stays put, so a page never scrolls its own header away
// and never stacks a document scrollbar on top of an inner one.
const useShellStyles = makeStyles({
  shell: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Chrome anchors the scroll to whatever was in view and puts the offset
    // back as the next route mounts, which undoes the reset below.
    overflowAnchor: 'none',
    // Pages size themselves against this region — see PAGE_HEIGHT_WITH_NAVBAR.
    position: 'relative',
  },
});

function Shell() {
  const classes = useShellStyles();
  const contentRef = useRef(null);
  const {pathname} = useLocation();

  // Every route now opens at the top. The scroll belongs to this region rather
  // than the document, so nothing puts it back the way a browser would have
  // for a page-level scroll. Keyed on the path and run after the new route has
  // committed — resetting on the history event alone is too early, the incoming
  // page is not mounted yet. Assigned rather than animated, so the route is
  // already at the top when it first paints.
  useLayoutEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [pathname]);

  return (
    <div className={classes.shell}>
      <div className={classes.header}>
        <Navbar/>
      </div>
      {!SKELETON_ROUTES.some(route => pathname.startsWith(route)) && <LoaderContainer/>}
      {window.Notification && <PushNotificationContainer />}
      <div className={classes.content} id={"app-scroll"} ref={contentRef}>
        <App/>
      </div>
      <ChatwootWidget/>
    </div>
  );
}

function Root() {
  return (
    <Provider store={store}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <Router history={history}>
            <CssBaseline/>
            <Shell/>
          </Router>
        </ThemeProvider>
      </StyledEngineProvider>
    </Provider>
  );
}

export default Root;
